/* eslint-disable no-undef */
import React from 'react'
import PropTypes from 'prop-types'
import TweetField from '../../../../tweet-input'
import { Field, formValueSelector } from 'redux-form'
import { connect } from 'react-redux'
import CheckboxField from 'components/form/checkbox'
import TagsInput from 'components/form/tagsinput'
import withFieldError from 'components/form/with-error'
import {
  pipe,
  map,
  filter,
  split,
  flatten,
  complement,
  isEmpty,
  defaultTo
} from 'ramda'

const selector = formValueSelector('widget')

export const TweetIdInput = ({
  input: { value, onChange, ...input },
  meta: { error, touched }
}) => (
  <div className={error && touched && 'has-error'}>
    <TagsInput
      inputProps={{
        placeholder: 'Enter Tweet IDs',
        className: 'react-tagsinput-input input-medium',
        ...input,
        onBlur: input.onBlur.bind(input, value)
      }}
      addOnBlur
      value={value}
      onChange={onChange}
    />
  </div>
)

const parseCsv = pipe(
  split('\n'),
  filter(complement(isEmpty)),
  map(split(',')),
  map(row => ({
    tweetId: row[0],
    weight: parseInt(row[1], 10),
    isFinalTweet: row[2] === 'true'
  }))
)

const normalizeCsv = pipe(parseCsv, flatten)

const handleFileChange = (input, file) => {
  const reader = new FileReader()
  reader.onload = e => {
    const value = e.target.result
    input.onChange(normalizeCsv(value))
  }
  reader.readAsText(file)
}

export const TweetIdsFileInput = ({
  input: { value, onChange },
  meta: { error, touched }
}) => {
  return (
    <div>
      <div className={error && touched && 'has-error'}>
        <div className='form-group'>
          <label htmlFor='tweetIdsFileInput'>Upload CSV file:</label>
          <input
            type='file'
            id='tweetIdsFileInput'
            className='form-control-file'
            onChange={e => handleFileChange({ onChange }, e.target.files[0])}
          />
        </div>
        {error && touched && <span className='help-block'>{error}</span>}
      </div>
      {value && value.length > 0 && (
        <div className='form-group'>
          <label htmlFor='tweetIdsFileInput'>Tweet IDs:</label>
          <ul>
            {value.map((tweet, index) => (
              <li key={index}>{tweet.tweetId}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

TweetIdsFileInput.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any
}

TweetIdInput.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any
}

export const normalize = pipe(
  map(split(' ')),
  flatten,
  filter(complement(isEmpty))
)

const SendTweetField = ({
  name,
  isRandomResponse,
  isMosaicConsent = false,
  hasResponseCap
}) => {
  return (
    <div className='row'>
      <div className='br-has-checkbox col-sm-12'>
        <Field
          name={`${name}.isRandomResponse`}
          component={CheckboxField}
          label='Random Response'
          className='icheckbox_square'
        />
        <Field
          name={`${name}.isMosaicConsent`}
          component={CheckboxField}
          label='Mosaic Consent'
          className='icheckbox_square'
          input={{ value: isMosaicConsent }}
        />
        {!isRandomResponse && (
          <div>
            {' '}
            <TweetField
              messageName={`${name}.message`}
              urlName={`${name}.attachmentUrl`}
              cardUriName={`${name}.cardUri`}
              isTweet
              isRandomResponse={`${name}.isRandomResponse`}
            />
          </div>
        )}
        {isRandomResponse && (
          <div>
            <Field
              name={`${name}.randomResponseTweetIds`}
              component={TweetIdsFileInput}
              normalize={defaultTo([])}
            />
            <Field
              name={`${name}.primary`}
              component='input'
              type='hidden'
              value
            />
            <Field
              name={`${name}.allowMultiple`}
              component={CheckboxField}
              label='Allow Multiple Non Duplicate Responses to the same end user'
              className='icheckbox_square'
            />
            <Field
              name={`${name}.hasResponseCap`}
              component={CheckboxField}
              label='Send a final Tweet after a specific number of responses'
              className='icheckbox_square'
            />
            {hasResponseCap && (
              <Field
                name={`${name}.responseCap`}
                component='input'
                type='number'
                className='form-control'
                placeholder='Enter the number of responses'
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

SendTweetField.propTypes = {
  name: PropTypes.string,
  isRandomResponse: PropTypes.bool,
  isMosaicConsent: PropTypes.bool,
  hasResponseCap: PropTypes.bool
}

const ConnectedReplyField = connect((state, props) => ({
  isRandomResponse: selector(state, `${props.name}.isRandomResponse`),
  isMosaicConsent: selector(state, `${props.name}.isMosaicConsent`),
  hasResponseCap: selector(state, `${props.name}.hasResponseCap`)
}))(SendTweetField)

const toNumber = value =>
  value === '' || value == null ? value : Number(value)

// A negative target is meaningless and is silently treated as no target (which
// drops the target and the lose reply), so reject it on both save paths.
export const nonNegative = value =>
  value !== '' && value != null && Number(value) < 0
    ? 'Must be zero or greater'
    : undefined

// Input that surfaces redux-form validation. redux-form drops `meta` on the
// built-in string `input` component, so a validator message never renders; a
// dedicated component paired with withFieldError shows it.
export const TargetInput = ({
  input,
  meta: { touched, error },
  type = 'text',
  min,
  placeholder
}) => (
  <input
    {...input}
    type={type}
    min={min}
    placeholder={placeholder}
    autoComplete='off'
    className={'form-control ' + (touched && error ? 'has-error' : '')}
  />
)

TargetInput.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any,
  type: PropTypes.string,
  min: PropTypes.string,
  placeholder: PropTypes.string
}

const TargetField = withFieldError(TargetInput)

// The stop action can send one reply to everyone, or split into a "win" reply
// (finished under the target time) and a "lose" reply. Setting a target time
// counts goals under it; ticking the lose-reply checkbox then reveals a second
// reply for fans at or over the time. Without the checkbox the target is
// count-only, so a blank (invalid) lose reply never blocks saving.
const StopSpeedThreadForm = ({ name, targetSeconds, enableLoseReply }) => {
  const hasTarget = Number(targetSeconds) > 0
  return (
    <div>
      <div className='form-group row'>
        <label className='col-sm-4 col-md-3 control-label'>
          Target time (seconds)
        </label>
        <div className='col-sm-8 col-md-9'>
          <Field
            name={`${name}.targetSeconds`}
            component={TargetField}
            type='number'
            min='0'
            placeholder='e.g. 8'
            normalize={toNumber}
            validate={nonNegative}
          />
          <span className='help-block'>
            Fans finishing under this time score a goal. Leave the target blank
            to send one reply and count every completion.
          </span>
        </div>
      </div>

      {hasTarget && (
        <h5 className='bold uppercase'>Win reply (finished under target)</h5>
      )}
      <ConnectedReplyField name={name} />

      {hasTarget && (
        <div className='br-has-checkbox'>
          <Field
            name={`${name}.enableLoseReply`}
            component={CheckboxField}
            label='Send a different reply to fans at or over the target'
            className='icheckbox_square'
          />
        </div>
      )}

      {hasTarget && enableLoseReply && (
        <div>
          <h5 className='bold uppercase'>Lose reply (target or over)</h5>
          <ConnectedReplyField name={`${name}.loseReply`} />
        </div>
      )}
    </div>
  )
}

StopSpeedThreadForm.propTypes = {
  name: PropTypes.string,
  targetSeconds: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  enableLoseReply: PropTypes.bool
}

const StopSpeedThreadWithAutoResponse = connect((state, props) => ({
  targetSeconds: selector(state, `${props.name}.targetSeconds`),
  enableLoseReply: selector(state, `${props.name}.enableLoseReply`)
}))(StopSpeedThreadForm)

export default StopSpeedThreadWithAutoResponse



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-triggers-actions/actions/twitter/stop-speed-thread/index.js